export var Result;
(function (Result) {
    Result[Result["Win"] = 0] = "Win";
    Result[Result["Draw"] = 1] = "Draw";
    Result[Result["Loss"] = 2] = "Loss";
})(Result || (Result = {}));
export const getPlayerResult = (playerId, intraSquadMatch) => {
    const result = intraSquadMatch.intraSquadMatchResult;
    if (!result)
        return undefined;
    const isDraw = result.teamAScore === result.teamBScore;
    if (isDraw)
        return Result.Draw;
    const winningTeamPlayers = result.teamAScore > result.teamBScore
        ? intraSquadMatch.teamAPlayers
        : intraSquadMatch.teamBPlayers;
    return winningTeamPlayers?.some((p) => p.id === playerId)
        ? Result.Win
        : Result.Loss;
};
export const aggregatePlayerIntraSquadMatchStats = (intraSquadMatches) => {
    return intraSquadMatches.reduce((statsMap, match) => {
        const players = [
            ...(match.teamAPlayers || []),
            ...(match.teamBPlayers || []),
        ];
        players.forEach((player) => {
            const playerId = player.id;
            if (playerId === undefined)
                return;
            // Calculate match-specific stats
            const resultStats = calculatePlayerResultStats(playerId, match);
            const playerStats = match.playerIntraSquadMatchStats?.find((stat) => stat.playerId === playerId);
            // Create new stats or update existing ones
            const existingStats = statsMap[playerId] || {
                matchesPlayed: 0,
                goalsScored: 0,
                yellowCards: 0,
                redCards: 0,
                wins: 0,
                losses: 0,
                draws: 0,
            };
            statsMap[playerId] = {
                matchesPlayed: existingStats.matchesPlayed + 1,
                goalsScored: existingStats.goalsScored + (playerStats?.goalsScored || 0),
                yellowCards: existingStats.yellowCards + (playerStats?.yellowCards || 0),
                redCards: existingStats.redCards + (playerStats?.redCards || 0),
                wins: existingStats.wins + resultStats.wins,
                losses: existingStats.losses + resultStats.losses,
                draws: existingStats.draws + resultStats.draws,
            };
        });
        return statsMap;
    }, {});
};
const calculatePlayerResultStats = (playerId, intraSquadMatch) => {
    const result = getPlayerResult(playerId, intraSquadMatch);
    return {
        wins: result === Result.Win ? 1 : 0,
        draws: result === Result.Draw ? 1 : 0,
        losses: result === Result.Loss ? 1 : 0,
    };
};
export const findPlayerIntraSquadMatchStatByPlayerId = (stats) => {
    return (id) => {
        return stats.find((stat) => stat.playerId === id);
    };
};
export const findPlayerExternalMatchStatByPlayerId = (stats) => {
    return (id) => {
        return stats.find((stat) => stat.playerId === id);
    };
};
export const findPlayerIntraSquadTournamentMatchStatByPlayerId = (stats) => {
    return (id) => {
        return stats.find((stat) => stat.playerId === id);
    };
};
export const findPlayerLeagueMatchStatByPlayerAndTeam = (stats) => {
    return (playerId, teamId) => {
        return stats.find((stat) => stat.playerId === playerId && stat.teamId === teamId);
    };
};
export const aggregatePlayerStats = (stats, aggregateFilter) => {
    const { years, types } = aggregateFilter;
    const totals = reducePlayerStats(stats, (item) => item.stats, (s) => (!years.length || years.includes(s.year)) &&
        (!types.length || types.includes(s.type)), (acc, s) => {
        acc.matchesPlayed += s.matchesPlayed;
        acc.goalsScored += s.goalsScored;
        acc.redCards += s.redCards;
        acc.yellowCards += s.yellowCards;
        acc.wins += s.wins;
        acc.draws += s.draws;
        acc.losses += s.losses;
    }, (player) => ({
        player,
        matchesPlayed: 0,
        goalsScored: 0,
        redCards: 0,
        yellowCards: 0,
        wins: 0,
        draws: 0,
        losses: 0,
        winPercentage: null,
    }));
    // Win % is over decided results (W+D+L), not matches played — a match can be
    // played without a recorded result.
    for (const total of totals) {
        const decided = total.wins + total.draws + total.losses;
        total.winPercentage = decided > 0 ? total.wins / decided : null;
    }
    return totals;
};
export const aggregatePlayerHonors = (stats, aggregateFilter) => {
    const { years } = aggregateFilter;
    return reducePlayerStats(stats, (item) => item.honors, (r) => !years.length || years.includes(r.year), (acc, r) => {
        acc.first += r.first;
        acc.second += r.second;
        acc.third += r.third;
        acc.participated += r.participated;
    }, (player) => ({
        player,
        first: 0,
        second: 0,
        third: 0,
        participated: 0,
    }));
};
function reducePlayerStats(input, getSubItems, shouldInclude, reducer, createInitial) {
    if (!input)
        return [];
    const totals = new Map();
    for (const item of input) {
        const id = item.player.id;
        if (id === undefined)
            continue;
        const player = item.player;
        for (const sub of getSubItems(item)) {
            if (!shouldInclude(sub))
                continue;
            if (!totals.has(id)) {
                totals.set(id, createInitial(player));
            }
            reducer(totals.get(id), sub);
        }
    }
    return [...totals.values()];
}
